import { useState } from "react";
import { fmtS, fmtD, fmtN } from "@/lib/format";
import Cell from "@/components/quote/Cell";
import { Package, Wrench, ShieldHalf, Droplets, Ship, Hammer, Receipt, Plus, Trash2, PlusSquare, GripVertical, Copy } from "lucide-react";

const DEF_DESC = {
  forros: "ALQUILER DE FORROS PARA PUERTAS PRINCIPALES, INCLUYE FUNDA REMOVIBLE PARA HOJA + ESQUINEROS SEMI RIGIDOS PARA MARCOS.",
  silicona: "SUMINISTRO DE SELLADO CON SILICONA, PARA PUERTAS CONTRAPLACADAS TERMOLAMINADAS, INCLUYE MATERIAL.",
  flete: "INCREMENTO POR FLETE INTERNACIONAL.",
  reparacion: "SERVICIO DE REPARACIÓN DE QUIÑES, GOLPES Y RAYONES, OCACIONADOS EN OBRA; SOLO APLICA EN INCIDENTES QUE PUEDAN SER REPARADOS POR UN TODISTA.",
};

const DEF_ENABLED = { suministro: true, flete: true, instalacion: true, forros: true, silicona: true, reparacion: true, custom: false };

function SectionHead({ icon, title, currency, sectionKey, checked, onToggle }) {
  const Icon = icon;
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
      <label className="flex items-center gap-2 cursor-pointer" title="Incluir en el imprimible / habilitar edición">
        <input type="checkbox" data-testid={`section-toggle-${sectionKey}`} checked={checked} onChange={onToggle}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
      </label>
      <div className="h-7 w-7 rounded-md bg-emerald-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-emerald-600" />
      </div>
      <h3 className={`font-display font-bold ${checked ? "text-slate-900" : "text-slate-400"}`}>{title}</h3>
      <span className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
        {currency === "$" ? "Dólares $" : "Soles S/"}
      </span>
    </div>
  );
}

function Section({ icon, title, sec, currency, descEdit, sectionKey, checked, onToggle }) {
  const money = currency === "$" ? fmtD : fmtS;
  return (
    <div className={`bg-white border rounded-lg overflow-hidden mb-6 ${checked ? "border-slate-200" : "border-slate-200 opacity-70"}`} data-testid={`soles-section-${title}`}>
      <SectionHead icon={icon} title={title} currency={currency} sectionKey={sectionKey} checked={checked} onToggle={onToggle} />
      {!checked ? (
        <div className="px-4 py-3 text-xs text-slate-400">No incluida en el imprimible. Marca la casilla para agregarla y editarla.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-2 text-left w-16">Cód.</th>
                <th className="px-3 py-2 text-left">Descripción{descEdit ? " (editable)" : ""}</th>
                <th className="px-3 py-2 text-right w-16">Cant.</th>
                <th className="px-3 py-2 text-right w-32">Vr. Unitario</th>
                <th className="px-3 py-2 text-right w-32">Vr. Total</th>
              </tr>
            </thead>
            <tbody>
              {sec.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-600 align-top pt-2.5">{r.code}</td>
                  <td className="px-2 py-1.5 text-slate-700 text-xs max-w-xl">
                    {descEdit && i === 0 ? (
                      <div className="border border-slate-200 rounded-md">
                        <Cell type="text" testid={descEdit.testid} value={descEdit.value} onChange={descEdit.onChange} className="text-left text-xs" />
                      </div>
                    ) : r.description}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono align-top pt-2.5">{r.qty}</td>
                  <td className="px-3 py-1.5 text-right font-mono align-top pt-2.5">{money(r.unit)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-medium align-top pt-2.5">{money(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="text-sm">
              {sec.descuento > 0 && (
                <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-500">Descuento</td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-600">- {money(sec.descuento)}</td></tr>
              )}
              <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-600">Sub-total</td>
                <td className="px-3 py-1.5 text-right font-mono">{money(sec.subtotal)}</td></tr>
              <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-600">IGV (18%)</td>
                <td className="px-3 py-1.5 text-right font-mono">{money(sec.igv)}</td></tr>
              <tr className="bg-emerald-50">
                <td colSpan={4} className="px-3 py-2 text-right font-bold text-slate-900">Valor total</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{money(sec.total)}</td></tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function CustomSection({ sec, checked, onToggle, title, onTitle, rows, onRows }) {
  const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
  const setRow = (i, field, v) => onRows(rows.map((r, j) => j === i ? { ...r, [field]: v } : r));
  const addRow = () => onRows([...rows, { _rid: uid(), code: "", description: "", und: "UND", qty: 1, price: 0, currency: "S/" }]);
  const removeRow = (i) => onRows(rows.filter((_, j) => j !== i));
  const duplicateRow = (i) => { const next = [...rows]; next.splice(i + 1, 0, { ...rows[i], _rid: uid() }); onRows(next); };
  const move = (from, to) => {
    if (to < 0 || to >= rows.length || from === to) return;
    const next = [...rows]; const [m] = next.splice(from, 1); next.splice(to, 0, m); onRows(next);
  };
  const [dragIdx, setDragIdx] = useState(null);
  const rowFmt = (r) => (r.currency === "$" ? fmtD : fmtS)((Number(r.qty) || 0) * (Number(r.price) || 0));
  const pen = sec.pen || { rows: [], subtotal: 0, igv: 0, total: 0 };
  const usd = sec.usd || { rows: [], subtotal: 0, igv: 0, total: 0 };

  return (
    <div className={`bg-white border rounded-lg overflow-hidden mb-6 ${checked ? "border-slate-200" : "border-slate-200 opacity-70"}`} data-testid="soles-section-custom">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
        <input type="checkbox" data-testid="section-toggle-custom" checked={checked} onChange={onToggle}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
        <div className="h-7 w-7 rounded-md bg-emerald-100 flex items-center justify-center">
          <PlusSquare className="h-4 w-4 text-emerald-600" />
        </div>
        {checked ? (
          <input data-testid="custom-title" value={title} onChange={(e) => onTitle(e.target.value)} placeholder="Título de la sección"
            className="font-display font-bold text-slate-900 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-emerald-500 focus:outline-none" />
        ) : (
          <h3 className="font-display font-bold text-slate-400">{title || "Adicionales"}</h3>
        )}
        <span className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">S/ y $</span>
      </div>
      {!checked ? (
        <div className="px-4 py-3 text-xs text-slate-400">Sección personalizada en blanco. Marca la casilla para agregar conceptos, unidades y precios.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-1 py-2 w-6"></th>
                <th className="px-3 py-2 text-left w-20">Cód.</th>
                <th className="px-3 py-2 text-left">Concepto / Descripción</th>
                <th className="px-3 py-2 text-left w-20">Und</th>
                <th className="px-3 py-2 text-right w-20">Cant.</th>
                <th className="px-3 py-2 text-left w-16">Moneda</th>
                <th className="px-3 py-2 text-right w-28">Vr. Unitario</th>
                <th className="px-3 py-2 text-right w-28">Vr. Total</th>
                <th className="px-2 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r._rid || i} data-testid={`custom-row-${i}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${dragIdx === i ? "opacity-40" : ""}`}>
                  <td className="px-1 py-1 text-center align-middle cursor-grab active:cursor-grabbing" draggable
                    data-testid={`custom-drag-${i}`}
                    onDragStart={() => setDragIdx(i)} onDragEnd={() => setDragIdx(null)}
                    title="Arrastra para reordenar">
                    <GripVertical className="h-4 w-4 text-slate-300 mx-auto" />
                  </td>
                  <td className="px-1 py-1"><div className="border border-slate-200 rounded-md"><Cell type="text" testid={`custom-code-${i}`} value={r.code || ""} onChange={(v) => setRow(i, "code", v)} className="text-left text-xs" /></div></td>
                  <td className="px-1 py-1"><div className="border border-slate-200 rounded-md"><Cell type="text" testid={`custom-desc-${i}`} value={r.description || ""} onChange={(v) => setRow(i, "description", v)} className="text-left text-xs" /></div></td>
                  <td className="px-1 py-1"><div className="border border-slate-200 rounded-md"><Cell type="text" testid={`custom-und-${i}`} value={r.und || ""} onChange={(v) => setRow(i, "und", v)} className="text-left text-xs" /></div></td>
                  <td className="px-1 py-1"><div className="border border-slate-200 rounded-md"><Cell testid={`custom-qty-${i}`} value={r.qty ?? 0} onChange={(v) => setRow(i, "qty", v)} /></div></td>
                  <td className="px-1 py-1">
                    <select data-testid={`custom-cur-${i}`} value={r.currency || "S/"} onChange={(e) => setRow(i, "currency", e.target.value)}
                      className="w-full px-1.5 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white">
                      <option value="S/">S/</option>
                      <option value="$">$</option>
                    </select>
                  </td>
                  <td className="px-1 py-1"><div className="border border-slate-200 rounded-md"><Cell step={0.5} testid={`custom-price-${i}`} value={r.price ?? 0} onChange={(v) => setRow(i, "price", v)} /></div></td>
                  <td className="px-3 py-1.5 text-right font-mono font-medium">{rowFmt(r)}</td>
                  <td className="px-1 py-1">
                    <div className="flex items-center justify-center gap-1.5">
                      <button data-testid={`custom-dup-${i}`} onClick={() => duplicateRow(i)} className="text-slate-300 hover:text-emerald-600" title="Duplicar concepto"><Copy className="h-3.5 w-3.5" /></button>
                      <button data-testid={`custom-del-${i}`} onClick={() => removeRow(i)} className="text-slate-300 hover:text-red-500" title="Eliminar"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-4 text-center text-xs text-slate-400">Sin conceptos. Presiona “Agregar concepto”.</td></tr>
              )}
            </tbody>
            <tfoot className="text-sm">
              {pen.rows.length > 0 && (
                <>
                  <tr><td colSpan={7} className="px-3 py-1.5 text-right text-slate-600">Sub-total (S/)</td>
                    <td colSpan={2} className="px-3 py-1.5 text-right font-mono">{fmtS(pen.subtotal)}</td></tr>
                  <tr><td colSpan={7} className="px-3 py-1.5 text-right text-slate-600">IGV (18%) S/</td>
                    <td colSpan={2} className="px-3 py-1.5 text-right font-mono">{fmtS(pen.igv)}</td></tr>
                  <tr className="bg-emerald-50"><td colSpan={7} className="px-3 py-2 text-right font-bold text-slate-900">Valor total (S/)</td>
                    <td colSpan={2} className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtS(pen.total)}</td></tr>
                </>
              )}
              {usd.rows.length > 0 && (
                <>
                  <tr><td colSpan={7} className="px-3 py-1.5 text-right text-slate-600">Sub-total ($)</td>
                    <td colSpan={2} className="px-3 py-1.5 text-right font-mono">{fmtD(usd.subtotal)}</td></tr>
                  <tr><td colSpan={7} className="px-3 py-1.5 text-right text-slate-600">IGV (18%) $</td>
                    <td colSpan={2} className="px-3 py-1.5 text-right font-mono">{fmtD(usd.igv)}</td></tr>
                  <tr className="bg-slate-100"><td colSpan={7} className="px-3 py-2 text-right font-bold text-slate-900">Valor total ($)</td>
                    <td colSpan={2} className="px-3 py-2 text-right font-mono font-bold text-slate-900">{fmtD(usd.total)}</td></tr>
                </>
              )}
            </tfoot>
          </table>
          <div className="px-4 py-2 border-t border-slate-100">
            <button data-testid="custom-add-row" onClick={addRow} className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
              <Plus className="h-4 w-4" /> Agregar concepto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SolesTab({ soles, editable, params, onParam, isAdmin, dollarParams, onDollar,
  instalacionDesc, onInstalacionDesc, onDoorInst, references, sections, onSectionToggle }) {
  const dp = dollarParams || {};
  const sumRef = references?.suministro || [];
  const instRef = references?.instalacion || [];
  const isOn = (k) => (sections && sections[k] !== undefined ? !!sections[k] : DEF_ENABLED[k]);
  const toggle = (k) => onSectionToggle(k, !isOn(k));
  const custom = soles.custom || { rows: [], total_pen: 0, total_usd: 0, pen: {}, usd: {} };
  const instQty = (soles.instalacion.rows || []).reduce((a, r) => a + (Number(r.qty) || 0), 0);
  const instPerDoor = instQty ? soles.instalacion.subtotal / instQty : 0;
  const totalPuertas = ((soles.combined && soles.combined.rows) || soles.suministro.rows || [])
    .reduce((a, r) => a + (Number(r.qty) || 0), 0);

  const totalUSD = (isOn("suministro") ? soles.suministro.total : 0) + (isOn("flete") ? soles.flete.total : 0)
    + (isOn("custom") ? (custom.total_usd || 0) : 0);
  const totalPEN = (isOn("instalacion") ? soles.instalacion.total : 0) + (isOn("forros") ? soles.forros.total : 0)
    + (isOn("silicona") ? soles.silicona.total : 0) + (isOn("reparacion") ? soles.reparacion.total : 0)
    + (isOn("custom") ? (custom.total_pen || 0) : 0);

  return (
    <div>
      {editable && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="font-display font-bold text-slate-900 mb-3 text-sm uppercase tracking-wide">Parámetros de venta</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["Alquiler forros - precio (S/)", "forros_precio"],
              ["Alquiler forros - cantidad", "forros_cant"],
              ["Sello silicona - precio (S/)", "silicona_precio"],
              ["Sello silicona - cantidad", "silicona_cant"],
              ["Reparación quiñes - precio (S/)", "reparacion_precio"],
              ["Descuento suministro ($)", "descuento_suministro"],
              ["Descuento instalación (S/)", "descuento_instalacion"],
            ].map(([label, key]) => (
              <label key={key} className="block">
                <span className="text-[11px] text-slate-500">{label}</span>
                <div className="mt-1 border border-slate-200 rounded-md">
                  <Cell testid={`soles-param-${key}`} value={params[key] ?? ""} onChange={(v) => onParam(key, v)} />
                </div>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Deja la cantidad en 0 para usar el valor automático (puertas / contenedores). Usa las casillas de cada sección para incluirla o no en el imprimible.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-1">
        <p className="text-sm text-slate-500">Precios de venta finales para el cliente.</p>
        <div className="flex gap-6">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Total de puertas</p>
            <p className="font-mono text-lg font-extrabold text-slate-900" data-testid="soles-total-puertas">{fmtN(totalPuertas, 0)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Total en dólares</p>
            <p className="font-mono text-lg font-extrabold text-slate-900" data-testid="soles-total-usd">{fmtD(totalUSD)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Total en soles</p>
            <p className="font-mono text-lg font-extrabold text-emerald-600" data-testid="soles-total-pen">{fmtS(totalPEN)}</p>
          </div>
        </div>
      </div>

      {sumRef.length > 0 && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 mb-4" data-testid="suministro-ref">
          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-2">Referencia de precios · suministro (por dimensión)</p>
          <div className="overflow-x-auto">
            <table className="text-xs w-full max-w-lg">
              <thead>
                <tr className="text-[10px] uppercase text-slate-500">
                  <th className="text-left py-1 pr-4">Dimensión</th>
                  <th className="text-right py-1 px-3">$ Resto de clientes</th>
                  <th className="text-right py-1 pl-3">$ Solo PDK</th>
                </tr>
              </thead>
              <tbody>
                {sumRef.map((r, i) => (
                  <tr key={i} className="border-t border-emerald-100">
                    <td className="py-1 pr-4 text-slate-700">{r.dimension}</td>
                    <td className="py-1 px-3 text-right font-mono text-slate-800">{r.resto != null ? `$ ${Number(r.resto).toFixed(2)}` : "—"}</td>
                    <td className="py-1 pl-3 text-right font-mono text-emerald-700">{r.pdk != null ? `$ ${Number(r.pdk).toFixed(2)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Section icon={Package} title="Suministro" sec={soles.suministro} currency="$"
        sectionKey="suministro" checked={isOn("suministro")} onToggle={() => toggle("suministro")} />
      <Section icon={Ship} title="Incremento flete internacional" sec={soles.flete} currency="$"
        sectionKey="flete" checked={isOn("flete")} onToggle={() => toggle("flete")}
        descEdit={{ testid: "flete-desc", value: params.flete_desc || DEF_DESC.flete, onChange: (v) => onParam("flete_desc", v) }} />

      {/* Instalación: descripción editable + cuadro de referencia */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h4 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wide mb-3">Referencia de instalación</h4>
        {instRef.length > 0 && (
          <div className="bg-amber-50/50 border border-amber-200 rounded-md p-3 mb-4" data-testid="instalacion-ref">
            <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-2">REF PDK conjunto · ajusta personal y meses hasta alcanzar estos valores</p>
            <div className="overflow-x-auto">
              <table className="text-xs w-full max-w-md">
                <thead>
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="text-left py-1 pr-4">Tipo</th>
                    <th className="text-right py-1 px-3">Conjunto</th>
                    <th className="text-right py-1 pl-3">PDK</th>
                  </tr>
                </thead>
                <tbody>
                  {instRef.map((r, i) => (
                    <tr key={i} className="border-t border-amber-100">
                      <td className="py-1 pr-4 text-slate-700">{r.label}</td>
                      <td className="py-1 px-3 text-right font-mono text-slate-800">{r.conjunto != null ? Number(r.conjunto).toFixed(0) : "—"}</td>
                      <td className="py-1 pl-3 text-right font-mono text-amber-700">{r.pdk != null ? Number(r.pdk).toFixed(0) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <label className="block mb-3">
          <span className="text-[11px] text-slate-500">Descripción por defecto de instalación (se aplica a las puertas sin texto propio)</span>
          <textarea data-testid="soles-instalacion-desc" rows={2} value={instalacionDesc || ""}
            onChange={(e) => onInstalacionDesc(e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-y" />
        </label>
        {isAdmin ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="block"><span className="text-[11px] text-slate-500">Cantidad de personal</span>
              <div className="mt-1 border border-emerald-200 rounded-md bg-emerald-50/40"><Cell testid="ref-personal" value={dp.personal ?? ""} onChange={(v) => onDollar("personal", v)} className="text-left" /></div></label>
            <label className="block"><span className="text-[11px] text-slate-500">Sueldo mínimo (S/)</span>
              <div className="mt-1 border border-emerald-200 rounded-md bg-emerald-50/40"><Cell testid="ref-sueldo" value={dp.sueldo_minimo ?? ""} onChange={(v) => onDollar("sueldo_minimo", v)} className="text-left" /></div></label>
            <label className="block"><span className="text-[11px] text-slate-500">Meses del proyecto</span>
              <div className="mt-1 border border-emerald-200 rounded-md bg-emerald-50/40"><Cell testid="ref-meses" value={dp.meses ?? ""} onChange={(v) => onDollar("meses", v)} className="text-left" /></div></label>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Total instalación (S/)</p>
              <p className="font-mono font-semibold text-emerald-600 text-sm mt-0.5" data-testid="ref-inst-total">{fmtS(soles.instalacion.total)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5" data-testid="ref-inst-perdoor">≈ {fmtS(instPerDoor)} / puerta sin IGV (compara con REF PDK)</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <label className="block"><span className="text-[11px] text-slate-500">Cantidad de personal</span>
              <div className="mt-1 border border-emerald-200 rounded-md bg-emerald-50/40"><Cell testid="ref-personal" value={dp.personal ?? ""} onChange={(v) => onDollar("personal", v)} className="text-left" /></div></label>
            <label className="block"><span className="text-[11px] text-slate-500">Meses del proyecto</span>
              <div className="mt-1 border border-emerald-200 rounded-md bg-emerald-50/40"><Cell testid="ref-meses" value={dp.meses ?? ""} onChange={(v) => onDollar("meses", v)} className="text-left" /></div></label>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Total instalación (S/)</p>
              <p className="font-mono font-semibold text-emerald-600 text-sm mt-0.5" data-testid="ref-inst-total">{fmtS(soles.instalacion.total)}</p>
              <p className="text-[10px] text-slate-400 mt-0.5" data-testid="ref-inst-perdoor">≈ {fmtS(instPerDoor)} / puerta sin IGV (compara con REF PDK)</p>
            </div>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">Ajusta personal y meses hasta llegar al valor de instalación deseado.</p>
      </div>

      <div className={`bg-white border rounded-lg overflow-hidden mb-6 ${isOn("instalacion") ? "border-slate-200" : "border-slate-200 opacity-70"}`} data-testid="soles-section-Instalación">
        <SectionHead icon={Wrench} title="Instalación" currency="S/" sectionKey="instalacion" checked={isOn("instalacion")} onToggle={() => toggle("instalacion")} />
        {!isOn("instalacion") ? (
          <div className="px-4 py-3 text-xs text-slate-400">No incluida en el imprimible. Marca la casilla para agregarla y editarla.</div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-2 text-left w-16">Cód.</th>
                <th className="px-3 py-2 text-left">Descripción (editable por puerta)</th>
                <th className="px-3 py-2 text-right w-16">Cant.</th>
                <th className="px-3 py-2 text-right w-32">Vr. Unitario</th>
                <th className="px-3 py-2 text-right w-32">Vr. Total</th>
              </tr>
            </thead>
            <tbody>
              {soles.instalacion.rows.map((r) => (
                <tr key={r.door_id || r.code} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-600 align-top pt-2.5">{r.code}</td>
                  <td className="px-2 py-1.5">
                    <div className="border border-slate-200 rounded-md">
                      <Cell type="text" testid={`inst-desc-${r.door_id}`} value={r.base_desc || ""}
                        onChange={(v) => onDoorInst(r.door_id, v)} className="text-left text-xs" />
                    </div>
                    {(r.width || r.height) ? <span className="text-[10px] text-slate-400 pl-1">{Number(r.width).toFixed(2)}m x {Number(r.height).toFixed(2)}m</span> : null}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono align-top pt-2.5">{r.qty}</td>
                  <td className="px-3 py-1.5 text-right font-mono align-top pt-2.5">{fmtS(r.unit)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-medium align-top pt-2.5">{fmtS(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="text-sm">
              {soles.instalacion.descuento > 0 && (
                <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-500">Descuento</td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-600">- {fmtS(soles.instalacion.descuento)}</td></tr>
              )}
              <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-600">Sub-total</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtS(soles.instalacion.subtotal)}</td></tr>
              <tr><td colSpan={4} className="px-3 py-1.5 text-right text-slate-600">IGV (18%)</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtS(soles.instalacion.igv)}</td></tr>
              <tr className="bg-emerald-50"><td colSpan={4} className="px-3 py-2 text-right font-bold text-slate-900">Valor total</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtS(soles.instalacion.total)}</td></tr>
            </tfoot>
          </table>
        </div>
        )}
      </div>

      <Section icon={ShieldHalf} title="Alquiler de forros" sec={soles.forros} currency="S/"
        sectionKey="forros" checked={isOn("forros")} onToggle={() => toggle("forros")}
        descEdit={{ testid: "forros-desc", value: params.forros_desc || DEF_DESC.forros, onChange: (v) => onParam("forros_desc", v) }} />
      <Section icon={Droplets} title="Sello de silicona" sec={soles.silicona} currency="S/"
        sectionKey="silicona" checked={isOn("silicona")} onToggle={() => toggle("silicona")}
        descEdit={{ testid: "silicona-desc", value: params.silicona_desc || DEF_DESC.silicona, onChange: (v) => onParam("silicona_desc", v) }} />
      <Section icon={Hammer} title="Reparación de quiñes" sec={soles.reparacion} currency="S/"
        sectionKey="reparacion" checked={isOn("reparacion")} onToggle={() => toggle("reparacion")}
        descEdit={{ testid: "reparacion-desc", value: params.reparacion_desc || DEF_DESC.reparacion, onChange: (v) => onParam("reparacion_desc", v) }} />

      <CustomSection sec={custom} checked={isOn("custom")} onToggle={() => toggle("custom")}
        title={params.custom_title ?? "Adicionales"} onTitle={(v) => onParam("custom_title", v)}
        rows={params.custom_rows || []} onRows={(rows) => onParam("custom_rows", rows)} />

      <div className="text-[11px] text-slate-400 flex items-center gap-1.5 px-1">
        <Receipt className="h-3.5 w-3.5" /> Suministro y flete se cotizan en dólares; instalación, forros, silicona, reparación y adicionales en soles.
      </div>
    </div>
  );
}
